(ns ballot-scout-v2-frontend.dashboard.views.current-ballot-location
  (:require
   [ballot-scout-v2-frontend.components.link :refer [link]]
   [ballot-scout-v2-frontend.dashboard.events :as e.dashboard]
   [ballot-scout-v2-frontend.dashboard.subs :as s.dashboard]
   [ballot-scout-v2-frontend.dashboard.views.fragments :as fragments]
   [ballot-scout-v2-frontend.request-helpers :as request-helpers]
   [ballot-scout-v2-frontend.subs :as subs]
   [ballot-scout-v2-frontend.util :as util]
   [goog.math :as math]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [reagent.dom.server :as server]))

(def us-geographic-center
  {:lat 39.833
   :lng -98.583})

(def default-map-options
  {:center us-geographic-center
   :mapTypeControl false
   :streetViewControl false
   :zoom 5
   :minZoom 0
   :maxZoom 10})

(def icon-fill-color
  "Ballot Scout compass red."
  "#f84239")

(def icon-stroke-color
  "Ballot Scout compass blue."
  "#00b5e1")

(defn- map-icon
  "Generates a Google Maps icon data structure sized appropriately for
  representing `n` mailings."
  [n]
  (let [max-n 100]
    {:path js/google.maps.SymbolPath.CIRCLE
     :scale (math/clamp (math/lerp 10 30 (/ n max-n)) 10 30)
     :fillOpacity 0.75
     :fillColor icon-fill-color
     :strokeColor icon-stroke-color
     :strokeWeight (math/clamp (math/lerp 2 5 (/ n max-n)) 2 5)}))

(defn- map-label
  "Generates a Google Maps label data structure with `text`."
  [text]
  {:text text
   :fontWeight "bold"})

(defn- ->info-window
  "Constructs a new google.maps.InfoWindow"
  ([]
   (->info-window #js {}))
  ([opts]
   (js/google.maps.InfoWindow. opts)))

(defn- ->lat-lng-bounds
  "Constructs a new google.maps.LatLngBounds."
  []
  (js/google.maps.LatLngBounds.))

(def info-window-ordering
  [:disposition/created
   :disposition/mailed
   :voter-bound/mailed
   :voter-bound/in-transit
   :voter-bound/out-for-delivery
   :voter-bound/delivered
   :office-bound/mailed
   :office-bound/in-transit
   :office-bound/out-for-delivery
   :office-bound/delivered
   :disposition/received
   :disposition/accepted
   :disposition/flagged
   :usps/returned])

(defn- info-window-html
  "Hiccup data structure for google.maps.InfoWindow HTML for `zipcode`."
  [loc]
  (let [mailing-statuses (->> loc
                              :ballots
                              util/combine-returned-statuses
                              (sort-by #(.indexOf info-window-ordering (key %))))
        {:keys [city state]} loc
        basic-voter-lookup-params
        {:partner-slug (:partner/slug @(rf/subscribe [::subs/current-partner]))
         :election-slug (:election/slug @(rf/subscribe [::subs/current-election]))}
        voter-lookup-route
        (if @(rf/subscribe [::subs/on-partner-admin-page?])
          {:route-name :ballot-scout-v2-frontend.routes/partner-voter-lookup
           :params basic-voter-lookup-params}
          {:route-name :ballot-scout-v2-frontend.routes/jurisdiction-voter-lookup
           :params (assoc basic-voter-lookup-params
                          :jurisdiction-slug
                          (:jurisdiction/slug @(rf/subscribe [::subs/current-jurisdiction])))})]
    [:section
     [link
      (merge voter-lookup-route
             {:query {:last-seen-city city :last-seen-state state}
              :class "button is-light is-family-sans-serif"})
      [:h4.heading-4 (str (util/capitalize-each-word city) ", " state)]]
     (into [:dl]
           (apply concat
                  (for [[status count] mailing-statuses]
                    [[:dt [link
                           (merge voter-lookup-route
                                  {:query {:last-seen-city city
                                           :last-seen-state state
                                           :status (util/ns-keyword->str status)}})
                           (util/backend-status->label status)]]
                     [:dd (str count)]])))]))

(defn total-ballots-count [location]
  (reduce + ((comp vals :ballots) location)))

(defn generate-markers
  [ballot-locations map-obj map-info-window map-markers]
  (reset! map-markers [])
  (doseq [loc ballot-locations]
    (when (and (number? (:latitude loc))
               (number? (:longitude loc)))
      (let [total (total-ballots-count loc)
            newmarker (js/google.maps.Marker.
                       (clj->js
                        {:map @map-obj
                         :label (map-label (str total))
                         :icon (map-icon total)
                         :position {:lat (:latitude loc)
                                    :lng (:longitude loc)}
                         :zIndex total}))]
        (swap! map-markers conj newmarker)
        (.addListener newmarker "click"
                      (fn [_]
                        (.close @map-info-window)
                        (.setOptions @map-info-window (clj->js
                                                       {:maxWidth "none"
                                                        :content (server/render-to-static-markup
                                                                  (info-window-html loc))}))
                        (.open @map-info-window @map-obj newmarker)))))))

(defn- fit-map-to-markers
  [map-obj map-markers]
  (let [known-markers @map-markers
        m @map-obj]
    (when-not (contains? #{:error :loading} known-markers)
      (when (not-empty known-markers)
        (let [bounds (->lat-lng-bounds)]
          (doseq [marker known-markers]
            (.extend bounds (.getPosition marker)))
          (.fitBounds m bounds))))))

;; inspired by http://day8.github.io/re-frame/Using-Stateful-JS-Components/#example-using-google-maps
(defn google-map
  [ballot-locations map-obj]
  (fn []
    (let [options (clj->js default-map-options)

          map-info-window (r/atom {})
          map-markers (r/atom {})

          update (fn [comp]
                   (when (> (count @ballot-locations) 0)
                     (generate-markers @ballot-locations map-obj map-info-window map-markers)
                     (fit-map-to-markers map-obj map-markers)
                     (reset! map-info-window (->info-window comp))))]
      (r/create-class
       {:reagent-render (fn []
                          [:div
                           [:div#map-canvas]])
        :component-did-mount (fn [cmp]
                               (let [canvas (.getElementById js/document "map-canvas")]
                                 (reset! map-obj (js/google.maps.Map. canvas options))
                                 (update cmp)))
        :component-did-update (fn [cmp]
                                (update cmp))
        :display-name "gmap-inner"}))))

(defn map-container
  []
  (fn []
    (let [map-obj (r/atom {})
          current-route @(rf/subscribe [:ballot-scout-v2-frontend.routes/current-route])
          current-election (:election/id @(rf/subscribe [::subs/current-election]))
          ballot-locations (rf/subscribe [::s.dashboard/ballots-by-location current-election])
          fetch-ballots-by-location-error
          (if @(rf/subscribe [::subs/on-partner-admin-page?])
            @(rf/subscribe [::request-helpers/failed-request
                            [::e.dashboard/fetch-ballots-by-location-and-partner current-route]])
            @(rf/subscribe [::request-helpers/failed-request
                            [::e.dashboard/fetch-ballots-by-location-and-jurisdiction current-route]]))]
      (cond
        (= @ballot-locations :loading)
        [:div [:div#map-loading.loading [:img {:src "/images/compass-100x100.png"}]
               [:div.loading-text "Loading ballot data..."]]]

        (and (seqable? @ballot-locations) (> (count @ballot-locations) 0))
        [:div [google-map ballot-locations map-obj]]

        :else
        (if (some? fetch-ballots-by-location-error)
          [:h4.heading-4.no-data "There was an error loading ballot location data."]
          [:h4.heading-4.no-data "Waiting for scan data, please check back later."])))))

(defn map-view
  []
  (if @(rf/subscribe [::s.dashboard/maps-lib-loaded])
    (fragments/title-section
     [:div.current-ballot-locations [:h2.title.heading-2 "Ballot Locations"]
      [map-container]])
    ;else -- handle no map loaded like v1
    [:div "Not loaded"]))
